import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Input, Text, Textarea, VStack } from '@chakra-ui/react';
import {ButtonVariant} from "../../core/enums";

interface EditableConfigRowProps {
  data: any;
  onCancel: () => void;
  onSave: (updated: {
    newValue: string;
    jiraTicket: string;
    jiraPurpose: string;
  }) => void;
}

// Helper function to safely render values as strings
const renderValueAsString = (value: any): string => {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length > 0 ? `[${value.join(', ')}]` : '[]';
    }
    return JSON.stringify(value);
  }

  return String(value);
};

const EditableConfigRow: React.FC<EditableConfigRowProps> = ({
                                                               data,
                                                               onCancel,
                                                               onSave
                                                             }) => {
    const [newValue, setNewValue] = useState(() => {
        // First check if there's an editableValue (for existing edits)
        if (data.editableValue && data.editableValue.trim() !== '') {
            return data.editableValue;
        }

        // For JSON configurations, check if defaultValue is an object
        if (data.defaultValue && typeof data.defaultValue === 'object' && data.defaultValue !== null) {
            return JSON.stringify(data.defaultValue, null, 2); // Pretty format with 2-space indentation
        }

        // Fallback to existing logic for non-JSON values
        if (Array.isArray(data.value)) {
            return data.value[0] || '';
        }

        return data.value || '';
    });

    const [jiraTicket, setJiraTicket] = useState('');
  const [jiraPurpose, setJiraPurpose] = useState('');

  const newValueRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const jiraTicketRef = useRef<HTMLInputElement>(null);
  const jiraPurposeRef = useRef<HTMLTextAreaElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const saveRef = useRef<HTMLButtonElement>(null);

  // Check if this is a JSON value to determine input type
    const isJsonValue =
        data.isJsonValue ||
        (data.defaultValue && typeof data.defaultValue === 'object' && data.defaultValue !== null);

  // Check if this is a tenant default
  const isTenantDefault = data.isTenantDefault || data.serviceLine === '*';

  // Validation for required fields
  const isJiraTicketValid = jiraTicket.trim().length > 0;
  const isJiraPurposeValid = jiraPurpose.trim().length > 0;
  const isSaveDisabled = !isJiraTicketValid || !isJiraPurposeValid;

  useEffect(() => {
    // Focus the first input when component mounts
    if (newValueRef.current) {
      newValueRef.current.focus();
      if (newValueRef.current instanceof HTMLInputElement) {
        newValueRef.current.select(); // Select all text for easy editing
      }
    }
  }, []);

  const handleKeyDown = (
      e: React.KeyboardEvent,
      nextRef?: React.RefObject<
          HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement
      >
  ) => {
    // Prevent AG Grid from handling tab navigation
    if (e.key === 'Tab') {
      e.stopPropagation();
      if (nextRef?.current && !e.shiftKey) {
        e.preventDefault();
        nextRef.current.focus();
        // Select all text in input fields
        if (nextRef.current instanceof HTMLInputElement) {
          nextRef.current.select();
        }
      }
    }

    // Allow Ctrl+A for select all
    if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
      e.stopPropagation();
    }
  };

  const handleShiftTab = (
      e: React.KeyboardEvent,
      prevRef?: React.RefObject<
          HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement
      >
  ) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.stopPropagation();
      if (prevRef?.current) {
        e.preventDefault();
        prevRef.current.focus();
        // Select all text in input fields
        if (prevRef.current instanceof HTMLInputElement) {
          prevRef.current.select();
        }
      }
    }
  };

  return (
      <Box whiteSpace="normal" wordBreak="break-word">
        <VStack align="stretch" spacing={2}>
          {isJsonValue ? (
              <Textarea
                  ref={newValueRef as React.RefObject<HTMLTextAreaElement>}
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  placeholder="Enter JSON value"
                  minHeight="120px"
                  fontFamily="monospace"
                  fontSize="sm"
                  onKeyDown={e => {
                    handleKeyDown(e, jiraTicketRef);
                    handleShiftTab(e);
                  }}
              />
          ) : (
              <Input
                  ref={newValueRef as React.RefObject<HTMLInputElement>}
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  placeholder="Enter new value"
                  onKeyDown={e => {
                    handleKeyDown(e, jiraTicketRef);
                    handleShiftTab(e);
                  }}
                  onFocus={e => e.target.select()}
              />
          )}

          <Text fontSize="xs" color="gray.500">
            {isJsonValue ? (
                <>
                  Please enter valid JSON format.
                  <br />
                  {isTenantDefault ? (
                      <>
                        This is the tenant-wide default value that applies to all service lines unless overridden.
                        <br />
                        Global default value is typically:{' '}
                        {renderValueAsString(data.defaultValue)}
                      </>
                  ) : (
                      <>
                        Default value is typically:{' '}
                        {renderValueAsString(data.defaultValue)}
                      </>
                  )}
                  <br />
                  Last updated by {data.updatedBy} on {data.updatedDate}
                </>
            ) : (
                <>
                  Please enter the correct format. It should look like: xxx-xxx-xxx.
                  <br />
                  {isTenantDefault ? (
                      <>
                        This is the tenant-wide default value that applies to all service lines unless overridden.
                        <br />
                        Global default value is typically:{' '}
                        {renderValueAsString(data.defaultValue)}
                      </>
                  ) : (
                      <>
                        Default value is typically:{' '}
                        {renderValueAsString(data.defaultValue)}
                      </>
                  )}
                  <br />
                  Last updated by {data.updatedBy} on {data.updatedDate}
                </>
            )}
          </Text>

          <Input
              ref={jiraTicketRef}
              value={jiraTicket}
              onChange={e => setJiraTicket(e.target.value)}
              placeholder="Enter JIRA ticket # (required)"
              onKeyDown={e => {
                handleKeyDown(e, jiraPurposeRef);
                handleShiftTab(e, newValueRef);
              }}
              onFocus={e => e.target.select()}
              isInvalid={!isJiraTicketValid && jiraTicket.length > 0}
              borderColor={
                !isJiraTicketValid && jiraTicket.length > 0 ? 'red.500' : undefined
              }
          />
          {!isJiraTicketValid && jiraTicket.length > 0 && (
              <Text fontSize="xs" color="red.500">
                JIRA ticket is required
              </Text>
          )}

          <Textarea
              ref={jiraPurposeRef}
              value={jiraPurpose}
              onChange={e => setJiraPurpose(e.target.value)}
              placeholder="Enter a comment (required)"
              onKeyDown={e => {
                handleKeyDown(e, cancelRef);
                handleShiftTab(e, jiraTicketRef);
              }}
              isInvalid={!isJiraPurposeValid && jiraPurpose.length > 0}
              borderColor={
                !isJiraPurposeValid && jiraPurpose.length > 0
                    ? 'red.500'
                    : undefined
              }
          />
          {!isJiraPurposeValid && jiraPurpose.length > 0 && (
              <Text fontSize="xs" color="red.500">
                Comment is required
              </Text>
          )}

          <Box display="flex" justifyContent="flex-end" gap={4} style={{ margin: '10px 0' }}>
            <Button
                ref={cancelRef}
                size="sm"
                onClick={onCancel}
                variant={ButtonVariant.SECONDARY}
                onKeyDown={e => {
                  handleKeyDown(e, saveRef);
                  handleShiftTab(e, jiraPurposeRef);
                }}>
              Cancel
            </Button>

            <Button
                ref={saveRef}
                size="sm"
                variant={ButtonVariant.PRIMARY}
                isDisabled={isSaveDisabled}
                onKeyDown={e => {
                  handleShiftTab(e, cancelRef);
                }}
                onClick={() =>
                    onSave({
                      newValue,
                      jiraTicket,
                      jiraPurpose
                    })
                }>
              Save
            </Button>
          </Box>
        </VStack>
      </Box>
  );
};

export default EditableConfigRow;
